import React, { useState, useEffect, useMemo } from 'react';
import {
  Modal,
  Button,
  Dropdown,
  DataTable,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  InlineNotification,
  SkeletonText,
  Checkbox,
  RadioButton,
  RadioButtonGroup,
} from '@carbon/react';
import { TrashCan } from '@carbon/react/icons';
import { useAtom, useAtomValue } from 'jotai';
import { rootResourceAtom } from 'atoms/root';
import {
  createGeologicIntervalTypeCurvesAtom,
  geologicIntervalsSearchAtom,
  typeCurveHeadersSearchAtom,
} from './atoms';

import styles from './add-relationship-modal.module.scss';

interface AddRelationshipModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
}

interface StagedRelationship {
  geologicIntervalId: number;
  geologicInterval: string;
  typeCurveFormation: string;
  createdBy: string;
}

const AddRelationshipModal: React.FC<AddRelationshipModalProps> = ({
  isOpen,
  onClose,
  onSaved,
}) => {
  const root = useAtomValue(rootResourceAtom);
  const userName = root?.session?.profile?.name || 'Unknown User';

  const [selectedGeoBasin, setSelectedGeoBasin] = useState<string>('');
  const [selectedIntervals, setSelectedIntervals] = useState<string[]>([]);
  const [isAllIntervalsSelected, setIsAllIntervalsSelected] = useState(false);
  const [selectedTypeCurveBasin, setSelectedTypeCurveBasin] =
    useState<string>('');
  const [selectedTypeCurveFormation, setSelectedTypeCurveFormation] =
    useState<string>('');
  const [stagedRelationships, setStagedRelationships] = useState<
    StagedRelationship[]
  >([]);
  const [error, setError] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const createAtom = useMemo(() => createGeologicIntervalTypeCurvesAtom, []);

  const geologicIntervalsSearch = useAtomValue(
    geologicIntervalsSearchAtom.searchAtom
  );
  const typeCurveHeadersSearch = useAtomValue(
    typeCurveHeadersSearchAtom.searchAtom
  );
  const [, createRelationships] = useAtom(createAtom);

  const geologicIntervals = useMemo(
    () => geologicIntervalsSearch.data?.results || [],
    [geologicIntervalsSearch.data?.results]
  );
  const typeCurveHeaders = useMemo(
    () => typeCurveHeadersSearch.data?.results || [],
    [typeCurveHeadersSearch.data?.results]
  );

  const isLoadingGeologicIntervals = geologicIntervalsSearch.loading;
  const isLoadingTypeCurveHeaders = typeCurveHeadersSearch.loading;

  const availableIntervals = useMemo(() => {
    const stagedIds = new Set(
      stagedRelationships.map((r) => r.geologicIntervalId)
    );
    return geologicIntervals.filter((interval) => !stagedIds.has(interval.id));
  }, [geologicIntervals, stagedRelationships]);

  const geologicBasins = useMemo(() => {
    const basins = [
      ...new Set(geologicIntervals.map((interval) => interval.basinName)),
    ];
    return basins.sort();
  }, [geologicIntervals]);

  const typeCurveBasins = useMemo(() => {
    const basins = [...new Set(typeCurveHeaders.map((header) => header.basin))];
    return basins.sort();
  }, [typeCurveHeaders]);

  const filteredGeologicIntervals = useMemo(() => {
    if (!selectedGeoBasin) return availableIntervals;
    return availableIntervals
      .filter((interval) => interval.basinName === selectedGeoBasin)
      .sort((a, b) => (a.positionalIndex || 0) - (b.positionalIndex || 0));
  }, [availableIntervals, selectedGeoBasin]);

  const filteredTypeCurveFormations = useMemo(() => {
    if (!selectedTypeCurveBasin) return [];
    return typeCurveHeaders
      .filter((header) => header.basin === selectedTypeCurveBasin)
      .map((header) => header.formation)
      .filter((formation, index, arr) => arr.indexOf(formation) === index)
      .sort();
  }, [typeCurveHeaders, selectedTypeCurveBasin]);

  useEffect(() => {
    if (geologicBasins.length > 0 && !selectedGeoBasin) {
      setSelectedGeoBasin(geologicBasins[0]);
    }
  }, [geologicBasins, selectedGeoBasin]);

  useEffect(() => {
    if (typeCurveBasins.length > 0 && !selectedTypeCurveBasin) {
      setSelectedTypeCurveBasin(typeCurveBasins[0]);
    }
  }, [typeCurveBasins, selectedTypeCurveBasin]);

  useEffect(() => {
    if (filteredTypeCurveFormations.length > 0 && !selectedTypeCurveFormation) {
      setSelectedTypeCurveFormation(filteredTypeCurveFormations[0]);
    }
  }, [filteredTypeCurveFormations, selectedTypeCurveFormation]);

  useEffect(() => {
    const allIntervalIds = filteredGeologicIntervals.map((interval) =>
      interval.id.toString()
    );
    const allSelected =
      allIntervalIds.length > 0 &&
      allIntervalIds.every((id) => selectedIntervals.includes(id));
    setIsAllIntervalsSelected(allSelected);
  }, [filteredGeologicIntervals, selectedIntervals]);

  const handleSelectAllIntervals = (
    _evt: React.ChangeEvent<HTMLInputElement>,
    { checked }: { checked: boolean }
  ) => {
    if (checked) {
      const allIntervalIds = filteredGeologicIntervals.map((interval) =>
        interval.id.toString()
      );
      setSelectedIntervals(allIntervalIds);
    } else {
      setSelectedIntervals([]);
    }
    setIsAllIntervalsSelected(checked);
  };

  const handleMatchSelected = () => {
    if (selectedIntervals.length === 0) {
      setError('Please select at least one geologic interval.');
      return;
    }

    if (!selectedTypeCurveFormation) {
      setError('Please select a type curve formation.');
      return;
    }

    const newRelationships = selectedIntervals.map((intervalId) => {
      const interval = geologicIntervals.find(
        (gi) => gi.id.toString() === intervalId
      );
      return {
        geologicIntervalId: parseInt(intervalId),
        geologicInterval: interval?.geologicInterval || '',
        typeCurveFormation: selectedTypeCurveFormation,
        createdBy: userName,
      };
    });

    setStagedRelationships((prev) => [...prev, ...newRelationships]);
    setSelectedIntervals([]);
    setError('');
  };

  const handleReset = () => {
    setStagedRelationships([]);
    setSelectedIntervals([]);
    setError('');
  };

  const handleRemoveRelationship = (index: number) => {
    setStagedRelationships((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    if (stagedRelationships.length === 0) {
      setError('No relationships to save.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    const payload = stagedRelationships.map((relationship) => ({
      geologic_interval_id: relationship.geologicIntervalId,
      type_curve_formation: relationship.typeCurveFormation,
      created_by: relationship.createdBy,
    }));

    createRelationships({
      type: 'POST',
      url: '/type_curve_formations',
      data: { geologic_interval_type_curves: payload },
      onSuccess: () => {
        setIsSubmitting(false);
        onSaved();
      },
      onError: () => {
        setIsSubmitting(false);
        setError('Failed to save relationships. Please try again.');
      },
    });
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setStagedRelationships([]);
      setSelectedIntervals([]);
      setError('');
      onClose();
    }
  };

  const tableHeaders = [
    { key: 'geologicInterval', header: 'Geologic Interval' },
    { key: 'typeCurveFormation', header: 'Type Curve Formation' },
    { key: 'createdBy', header: 'Created By' },
    { key: 'actions', header: '' },
  ];

  const tableRows = stagedRelationships.map((relationship, index) => ({
    id: index.toString(),
    geologicInterval: relationship.geologicInterval,
    typeCurveFormation: relationship.typeCurveFormation,
    createdBy: relationship.createdBy,
    actions: (
      <Button
        hasIconOnly
        kind="danger--ghost"
        size="sm"
        renderIcon={TrashCan}
        iconDescription="Remove relationship"
        tooltipAlignment="center"
        tooltipPosition="top"
        onClick={() => handleRemoveRelationship(index)}
        className={styles.removeButton}
      />
    ),
  }));

  return (
    <Modal
      open={isOpen}
      modalHeading="Add Geologic interval to Type Curve Relationship"
      passiveModal
      onRequestClose={handleClose}
      className={`${styles.modal} ${styles.addRelationshipModal}`}
      size="lg"
      aria-label="Add relationship modal"
    >
      <div className={styles.modalContent}>
        {error && (
          <InlineNotification
            kind="error"
            title="Error"
            subtitle={error}
            onCloseButtonClick={() => setError('')}
            className={styles.notification}
          />
        )}

        <div className={styles.formSection}>
          <div className={styles.dropdownRow}>
            <div className={styles.dropdownContainer}>
              <h3 className={styles.sectionHeader}>
                Select the Basin Grid Collection
              </h3>
              <p className={styles.requiredText}>
                All fields below are required
              </p>
              {isLoadingGeologicIntervals ? (
                <div
                  className={styles.loadingContainer}
                  data-testid="basin-loading"
                >
                  <SkeletonText heading />
                  <SkeletonText />
                </div>
              ) : (
                <Dropdown
                  id="geologic-basin-dropdown"
                  titleText="Basin"
                  label={
                    geologicBasins.length === 0
                      ? 'No basins available'
                      : 'Select Basin'
                  }
                  items={geologicBasins}
                  itemToString={(item) => item || ''}
                  selectedItem={selectedGeoBasin}
                  disabled={geologicBasins.length === 0}
                  onChange={({ selectedItem }) => {
                    setSelectedGeoBasin(selectedItem || '');
                    setSelectedIntervals([]);
                  }}
                  aria-describedby="geologic-basin-help"
                />
              )}
              {geologicBasins.length === 0 && !isLoadingGeologicIntervals && (
                <div id="geologic-basin-help" className={styles.helpText}>
                  No geologic interval basins found. Please check your data.
                </div>
              )}
            </div>

            <div className={styles.dropdownContainer}>
              <h3 className={styles.sectionHeader}>
                Select the Type Curve Formation
              </h3>
              <p className={styles.requiredText}>
                All fields below are required
              </p>
              {isLoadingTypeCurveHeaders ? (
                <div
                  className={styles.loadingContainer}
                  data-testid="type-curve-loading"
                >
                  <SkeletonText heading />
                  <SkeletonText />
                </div>
              ) : (
                <Dropdown
                  id="type-curve-basin-dropdown"
                  titleText="Type Curve Basin"
                  label={
                    typeCurveBasins.length === 0
                      ? 'No basins available'
                      : 'Select Basin'
                  }
                  items={typeCurveBasins}
                  itemToString={(item) => item || ''}
                  selectedItem={selectedTypeCurveBasin}
                  disabled={typeCurveBasins.length === 0}
                  onChange={({ selectedItem }) => {
                    setSelectedTypeCurveBasin(selectedItem || '');
                    setSelectedTypeCurveFormation('');
                  }}
                />
              )}
            </div>
          </div>

          <div className={styles.dropdownRow}>
            <div className={styles.dropdownContainer}>
              {isLoadingGeologicIntervals ? (
                <div
                  className={styles.loadingContainer}
                  data-testid="interval-loading"
                >
                  <SkeletonText heading />
                  <SkeletonText />
                </div>
              ) : (
                <div className={styles.tableList}>
                  <div className={styles.tableHeader}>
                    <Checkbox
                      id="select-all-intervals"
                      labelText="Geologic Interval"
                      checked={isAllIntervalsSelected}
                      onChange={handleSelectAllIntervals}
                      disabled={filteredGeologicIntervals.length === 0}
                      className={styles.checkboxListHeader}
                    />
                  </div>
                  <div className={styles.scrollableTableBody}>
                    {filteredGeologicIntervals.length === 0 ? (
                      <div className={styles.emptyListMessage}>
                        No intervals available
                      </div>
                    ) : (
                      filteredGeologicIntervals.map((interval) => (
                        <div
                          key={interval.id}
                          className={`${styles.tableRow} ${
                            selectedIntervals.includes(interval.id.toString())
                              ? styles.selectedRow
                              : ''
                          }`}
                        >
                          <Checkbox
                            id={`interval-${interval.id}`}
                            labelText={interval.geologicInterval}
                            checked={selectedIntervals.includes(
                              interval.id.toString()
                            )}
                            onChange={(
                              _evt,
                              { checked }: { checked: boolean }
                            ) => {
                              if (checked) {
                                setSelectedIntervals((prev) => [
                                  ...prev,
                                  interval.id.toString(),
                                ]);
                              } else {
                                setSelectedIntervals((prev) =>
                                  prev.filter(
                                    (id) => id !== interval.id.toString()
                                  )
                                );
                              }
                            }}
                          />
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className={styles.dropdownContainer}>
              {isLoadingTypeCurveHeaders ? (
                <div
                  className={styles.loadingContainer}
                  data-testid="formation-loading"
                >
                  <SkeletonText heading />
                  <SkeletonText />
                </div>
              ) : (
                <div className={styles.tableList}>
                  <div
                    className={`${styles.tableHeader} ${styles.typeCurveFormationHeader}`}
                  >
                    <span className={styles.tableHeaderText}>
                      Type Curve Formation
                    </span>
                  </div>
                  <div className={styles.scrollableTableBody}>
                    {filteredTypeCurveFormations.length === 0 ? (
                      <div className={styles.emptyListMessage}>
                        No formations available
                      </div>
                    ) : (
                      <RadioButtonGroup
                        name="type-curve-formation"
                        valueSelected={selectedTypeCurveFormation}
                        onChange={(value: string | number | undefined) => {
                          setSelectedTypeCurveFormation(String(value));
                        }}
                        className={styles.radioButtonGroup}
                      >
                        {filteredTypeCurveFormations.map((formation) => (
                          <RadioButton
                            key={formation}
                            id={`formation-${formation}`}
                            value={formation}
                            labelText={formation}
                            className={`${styles.radioButton} ${
                              selectedTypeCurveFormation === formation
                                ? styles.selectedRow
                                : ''
                            }`}
                          />
                        ))}
                      </RadioButtonGroup>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className={styles.matchButtonRow}>
            <Button
              kind="primary"
              onClick={handleMatchSelected}
              disabled={
                selectedIntervals.length === 0 ||
                !selectedTypeCurveFormation ||
                isLoadingGeologicIntervals ||
                isLoadingTypeCurveHeaders
              }
            >
              Match Selected
            </Button>
          </div>
        </div>

        <div className={styles.tableSection}>
          <div className={styles.tableSectionHeader}>
            <h4 className={styles.leftAlignedHeader}>
              Current Matched Relationships
            </h4>
            {stagedRelationships.length > 0 && (
              <p className={styles.tableSectionSubtitle}>
                These relationships will be saved when you click &quot;Add
                Matched Relationships&quot;
              </p>
            )}
          </div>

          <DataTable
            rows={
              stagedRelationships.length > 0
                ? tableRows
                : [
                    {
                      id: 'empty',
                      geologicInterval: '--',
                      typeCurveFormation: '--',
                      createdBy: '--',
                      actions: <div />,
                    },
                  ]
            }
            headers={tableHeaders}
          >
            {({
              rows,
              headers,
              getTableProps,
              getHeaderProps,
              getRowProps,
            }: any) => (
              <TableContainer>
                <Table {...getTableProps()} className={styles.table}>
                  <TableHead>
                    <TableRow>
                      {headers.map((header: any) => (
                        <TableHeader
                          key={header.key}
                          {...getHeaderProps({ header })}
                        >
                          {header.header}
                        </TableHeader>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row: any) => (
                      <TableRow key={row.id} {...getRowProps({ row })}>
                        {row.cells.map((cell: any) => (
                          <TableCell key={cell.id}>{cell.value}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DataTable>
        </div>
      </div>
      <div className={styles.bottomButtonRow}>
        <Button
          kind="tertiary"
          onClick={handleClose}
          disabled={isSubmitting}
          className={styles.bottomButton}
        >
          Cancel
        </Button>
        <Button
          kind="secondary"
          onClick={handleReset}
          disabled={stagedRelationships.length === 0}
          className={styles.bottomButton}
        >
          Reset
        </Button>
        <Button
          kind="primary"
          onClick={handleSave}
          disabled={isSubmitting || stagedRelationships.length === 0}
          className={styles.bottomButton}
        >
          {isSubmitting ? 'Saving...' : 'Add Matched Relationships'}
        </Button>
      </div>
    </Modal>
  );
};

export default AddRelationshipModal;
